import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError, formatINR, payViaGateway } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { CheckCircle, ArrowRight, ShieldCheck, FileText, SealCheck } from "@phosphor-icons/react";

const MODELS = [
  { id: "Growth Lock (A)", name: "Growth Lock", tag: "Model A", d: "Returns compound over a 60-month lock-in. Best for long-term wealth creation.", tenures: ["60 months"] },
  { id: "Periodic Distribution (B)", name: "Periodic Distribution", tag: "Model B", d: "Receive periodic distributions to your wallet. Choose your tenure.", tenures: ["12 months", "24 months", "36 months"] },
  { id: "Fixed + Maturity Premium (C)", name: "Fixed + Premium", tag: "Model C", d: "Fixed periodic returns plus a premium at maturity.", tenures: ["24 months", "36 months"] },
];

export default function Invest() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [brand, setBrand] = useState(null);
  const [rate, setRate] = useState(0.16);
  const [step, setStep] = useState(1);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [agree, setAgree] = useState(false);
  const [form, setForm] = useState({ principal: 600000, model: MODELS[1].id, tenure: "24 months" });

  useEffect(() => {
    if (user && user.kyc_status !== "verified") { navigate(`/kyc?next=/partner/invest/${slug}`); return; }
    api.get(`/brands/${slug}`).then(({ data }) => { setBrand(data); setForm((f) => ({ ...f, principal: data.ticket_min })); }).catch(() => toast.error("Brand not found"));
    api.get("/config").then(({ data }) => setRate(data.indicative_rate || 0.16)).catch(() => {});
  }, [slug, user, navigate]);

  const selectedModel = MODELS.find((m) => m.id === form.model);
  const indicativeAnnual = Math.round(form.principal * rate);

  const submitAndPay = async () => {
    if (!agree) return toast.error("Please e-sign the agreement to continue");
    setLoading(true);
    try {
      const { data: inv } = await api.post("/investments", { brand_slug: slug, principal: Number(form.principal), model: form.model, tenure: form.tenure, agreement_signed: true });
      const res = await payViaGateway({ purpose: "investment", amount: Number(form.principal), ref_id: inv.id, user });
      if (res.status === "paid") { setDone(true); }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); } finally { setLoading(false); }
  };

  if (!brand) return <div className="px-6 py-32 text-center text-ebg-muted font-data">Loading…</div>;

  if (done) {
    return (
      <div data-testid="invest-success" className="px-6 py-24 max-w-xl mx-auto text-center">
        <CheckCircle size={72} weight="fill" className="text-gold mx-auto" />
        <h1 className="font-display text-4xl text-white mt-6">You're now an Ecosystem Partner.</h1>
        <p className="text-ebg-muted mt-3">Your investment in <span className="text-white">{brand.name}</span> is active. Track returns in your portfolio.</p>
        <button onClick={() => navigate("/portfolio")} className="mt-8 btn-gold px-7 py-3 rounded-md">Go to Portfolio</button>
      </div>
    );
  }

  return (
    <div data-testid="invest-page" className="px-6 md:px-12 lg:px-20 py-12 md:py-16">
      <p className="eyebrow mb-2">Invest · {brand.name}</p>
      <h1 className="font-display text-4xl md:text-5xl text-white">Become a franchise partner.</h1>

      <div className="flex items-center gap-2 mt-8 mb-8 max-w-md">
        {["Configure", "e-Sign & Pay"].map((s, i) => (
          <div key={s} className="flex items-center gap-2 flex-1">
            <span className={`font-data text-xs w-6 h-6 rounded-full grid place-items-center ${step >= i + 1 ? "btn-gold" : "bg-white/10 text-ebg-muted"}`}>{i + 1}</span>
            <span className={`text-xs uppercase tracking-wider ${step >= i + 1 ? "text-white" : "text-ebg-muted"}`}>{s}</span>
            {i < 1 && <span className="flex-1 h-px bg-white/10" />}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-2xl p-6 md:p-8 shadow-2xl">
          {step === 1 ? (
            <div data-testid="invest-configure">
              <h2 className="font-display text-2xl text-navy-950 mb-4">Choose your model</h2>
              <div className="space-y-3">
                {MODELS.map((m) => (
                  <button key={m.id} type="button" onClick={() => setForm({ ...form, model: m.id, tenure: m.tenures[0] })} data-testid={`invest-model-${m.tag.replace(/\s/g,'').toLowerCase()}`}
                    className={`w-full text-left rounded-xl border p-4 transition-colors ${form.model === m.id ? "border-gold bg-gold/5" : "border-gray-200 hover:border-gray-300"}`}>
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-navy-950">{m.name} <span className="text-[0.6rem] uppercase tracking-wider text-gold ml-1">{m.tag}</span></p>
                      <span className={`w-4 h-4 rounded-full border-2 ${form.model === m.id ? "border-gold bg-gold" : "border-gray-300"}`} />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{m.d}</p>
                  </button>
                ))}
              </div>

              <h2 className="font-display text-2xl text-navy-950 mt-8 mb-3">Investment amount</h2>
              <input type="range" min={brand.ticket_min} max={brand.ticket_max} step={50000} value={form.principal} onChange={(e) => setForm({ ...form, principal: Number(e.target.value) })} data-testid="invest-amount-range" className="w-full accent-gold" />
              <div className="flex items-center justify-between mt-2">
                <span className="text-sm text-gray-500">{formatINR(brand.ticket_min)}</span>
                <span className="font-display text-3xl text-navy-950">{formatINR(form.principal)}</span>
                <span className="text-sm text-gray-500">{formatINR(brand.ticket_max)}</span>
              </div>

              <h2 className="font-display text-2xl text-navy-950 mt-8 mb-3">Tenure</h2>
              <div className="flex gap-3 flex-wrap">
                {selectedModel.tenures.map((t) => (
                  <button key={t} type="button" onClick={() => setForm({ ...form, tenure: t })} data-testid={`invest-tenure-${t.split(' ')[0]}`}
                    className={`px-4 py-2 rounded-md border text-sm transition-colors ${form.tenure === t ? "border-gold bg-gold/10 text-navy-950 font-medium" : "border-gray-200 text-gray-500 hover:border-gold"}`}>{t}</button>
                ))}
              </div>

              <button onClick={() => setStep(2)} data-testid="invest-continue-btn" className="w-full mt-8 btn-gold py-3.5 rounded-lg flex items-center justify-center gap-2">Continue to e-Sign <ArrowRight size={18} /></button>
            </div>
          ) : (
            <div data-testid="invest-esign">
              <h2 className="font-display text-2xl text-navy-950 mb-2 flex items-center gap-2"><FileText size={22} className="text-gold" /> Investment Agreement</h2>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 text-sm text-gray-600 max-h-60 overflow-y-auto leading-relaxed">
                <p className="font-medium text-navy-950">EBG FinCorp — Fractional Investment Agreement</p>
                <p className="mt-2">Investor: <b>{user?.name}</b> · Business Unit: <b>{brand.name}</b></p>
                <p>Model: <b>{selectedModel.name} ({selectedModel.tag})</b> · Tenure: <b>{form.tenure}</b></p>
                <p>Principal: <b>{formatINR(form.principal)}</b></p>
                <p className="mt-3">1. This is a fractional ownership commitment in an EBG-operated business unit.<br/>2. Returns are derived from net operating surplus and are <b>indicative, not guaranteed</b>.<br/>3. This is not a deposit, fund, or scheme. Final regulatory terms apply per counsel.<br/>4. Lock-in, distribution & early-exit terms follow the selected model.</p>
              </div>
              <label className="flex items-start gap-2 text-sm text-gray-600 mt-4 cursor-pointer">
                <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} data-testid="invest-esign-checkbox" className="mt-0.5 accent-gold" />
                I have read and <b className="mx-1">e-sign</b> this agreement electronically.
              </label>
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mt-4">
                <p className="text-xs text-amber-700">DEMO MODE — e-sign and payment are simulated (Razorpay sandbox stub).</p>
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={() => setStep(1)} className="px-5 py-3.5 rounded-lg border border-gray-200 text-gray-600 text-sm">Back</button>
                <button onClick={submitAndPay} disabled={loading} data-testid="invest-esign-pay-btn" className="flex-1 btn-gold py-3.5 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50">
                  <SealCheck size={18} /> {loading ? "Processing…" : `e-Sign & Pay ${formatINR(form.principal)}`}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* summary */}
        <aside className="lg:col-span-1">
          <div className="bg-navy-900 border border-gold/20 rounded-2xl p-6 lg:sticky lg:top-24">
            <h3 className="font-display text-2xl text-white mb-5">Summary</h3>
            <div className="space-y-4">
              {[["Business Unit", brand.name], ["Model", selectedModel.tag], ["Tenure", form.tenure], ["Principal", formatINR(form.principal)],
                ["Indicative Return", `${Math.round(rate*100)}% p.a.`], ["~ Annual (indicative)", formatINR(indicativeAnnual)]].map(([k, v]) => (
                <div key={k} className="flex items-center justify-between border-b border-white/10 pb-3">
                  <span className="text-sm text-ebg-muted">{k}</span><span className="text-sm text-white font-medium text-right">{v}</span>
                </div>
              ))}
            </div>
            <p className="text-[0.7rem] text-ebg-muted mt-4 leading-relaxed flex items-start gap-1.5"><ShieldCheck size={14} className="text-gold flex-shrink-0 mt-0.5" /> Returns are <b className="mx-1">indicative, not guaranteed</b>, and subject to business performance and due diligence.</p>
          </div>
        </aside>
      </div>
    </div>
  );
}
